import { motion } from 'framer-motion';
import { Shield, Lock, Server, Eye, FileCheck, Users, ArrowRight } from 'lucide-react';
import StaticPageLayout from '../../components/StaticPageLayout';
import { useThemeStore } from '../../store/theme';

const securityFeatures = [
  {
    icon: Lock,
    title: 'End-to-End Encryption',
    description:
      'All data is encrypted in transit using TLS 1.3 and at rest using AES-256 encryption. Your conversations and files are protected at every step.',
  },
  {
    icon: Server,
    title: 'Secure Infrastructure',
    description:
      'Our infrastructure is hosted on Cloudflare\'s global network with built-in DDoS protection, edge caching, and Web Application Firewall (WAF). Data is stored in Cloudflare D1 and R2 with encryption at rest.',
  },
  {
    icon: Eye,
    title: 'Privacy by Design',
    description:
      'We follow privacy-by-design principles. Data minimization, purpose limitation, and user consent are built into every feature.',
  },
  {
    icon: FileCheck,
    title: 'Compliance',
    description:
      'We maintain GDPR and CCPA compliance and are working toward SOC 2 Type II certification. Our practices are continuously reviewed and improved.',
  },
  {
    icon: Users,
    title: 'Access Controls',
    description:
      'Role-based access controls, multiple authentication methods (password, Google OAuth, magic link), per-tab session isolation, and team workspace permissions ensure only authorized users access your data.',
  },
  {
    icon: Shield,
    title: 'Vulnerability Management',
    description:
      'Continuous vulnerability scanning, penetration testing, and a responsible disclosure program help us identify and fix security issues quickly.',
  },
];

const certifications = [
  { name: 'GDPR', status: 'Compliant', year: '2026' },
  { name: 'CCPA', status: 'Compliant', year: '2026' },
  { name: 'SOC 2 Type II', status: 'In Progress', year: '2026' },
  { name: 'ISO 27001', status: 'Planned', year: '2026' },
];

const practices = [
  {
    title: 'Data Retention',
    items: [
      'Chat data retained only while account is active',
      'Automatic deletion 30 days after account closure',
      'Brain AI data deleted within 7 days of removal',
      'Audit logs retained for 1 year for security purposes',
    ],
  },
  {
    title: 'Employee Access',
    items: [
      'Strict need-to-know access policies',
      'Background checks for all employees',
      'Regular security training programs',
      'Access logging and monitoring',
    ],
  },
  {
    title: 'Incident Response',
    items: [
      '24/7 security monitoring and alerting',
      'Documented incident response procedures',
      'Maximum 72-hour breach notification',
      'Post-incident analysis and improvement',
    ],
  },
];

export default function Security() {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';

  return (
    <StaticPageLayout
      title="Security"
      subtitle="Your data security is our top priority. Learn how we protect your information."
    >
      {/* Security Features */}
      <section className="mb-16">
        <h2 className={`text-2xl font-bold mb-8 ${isDark ? 'text-white' : 'text-slate-900'}`}>How We Protect Your Data</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {securityFeatures.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`p-6 rounded-2xl border ${
                isDark ? 'bg-dark-800/50 border-white/5' : 'bg-white border-slate-200 shadow-sm'
              }`}
            >
              <div className="w-12 h-12 rounded-xl bg-green-500/10 flex items-center justify-center mb-4">
                <feature.icon className={`w-6 h-6 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
              </div>
              <h3 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>{feature.title}</h3>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>{feature.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Certifications */}
      <section className="mb-16">
        <h2 className={`text-2xl font-bold mb-8 ${isDark ? 'text-white' : 'text-slate-900'}`}>Certifications & Compliance</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {certifications.map((cert) => (
            <div
              key={cert.name}
              className={`p-6 rounded-2xl border text-center ${
                isDark ? 'bg-dark-800/50 border-white/5' : 'bg-white border-slate-200 shadow-sm'
              }`}
            >
              <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-4">
                <Shield className={`w-8 h-8 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
              </div>
              <h3 className={`font-semibold mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>{cert.name}</h3>
              <span
                className={`text-sm ${
                  cert.status === 'Certified' || cert.status === 'Compliant'
                    ? isDark ? 'text-green-400' : 'text-green-600'
                    : isDark ? 'text-yellow-400' : 'text-yellow-600'
                }`}
              >
                {cert.status}
              </span>
              <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>{cert.year}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Security Practices */}
      <section className="mb-16">
        <h2 className={`text-2xl font-bold mb-8 ${isDark ? 'text-white' : 'text-slate-900'}`}>Security Practices</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {practices.map((practice, index) => (
            <motion.div
              key={practice.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`p-6 rounded-2xl border ${
                isDark ? 'bg-dark-800/50 border-white/5' : 'bg-white border-slate-200 shadow-sm'
              }`}
            >
              <h3 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>{practice.title}</h3>
              <ul className="space-y-3">
                {practice.items.map((item) => (
                  <li key={item} className={`flex items-start gap-2 text-sm ${isDark ? 'text-gray-300' : 'text-slate-600'}`}>
                    <span className={`mt-0.5 ${isDark ? 'text-green-400' : 'text-green-600'}`}>✓</span>
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Data Processing */}
      <section className="mb-16">
        <h2 className={`text-2xl font-bold mb-8 ${isDark ? 'text-white' : 'text-slate-900'}`}>AI Data Processing</h2>
        <div className={`p-8 rounded-2xl border ${
          isDark ? 'bg-dark-800/50 border-white/5' : 'bg-white border-slate-200 shadow-sm'
        }`}>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>What We Do</h3>
              <ul className="space-y-3">
                {[
                  'Process your messages to generate AI responses',
                  'Store chat history for your convenience',
                  'Use your Brain AI data to personalize responses',
                  'Encrypt all data in transit and at rest',
                  'Automatically delete data after account closure',
                ].map((item) => (
                  <li key={item} className={`flex items-start gap-2 text-sm ${isDark ? 'text-gray-300' : 'text-slate-600'}`}>
                    <span className={`mt-0.5 ${isDark ? 'text-green-400' : 'text-green-600'}`}>✓</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>What We Don't Do</h3>
              <ul className="space-y-3">
                {[
                  'Share your data with third parties for marketing',
                  'Use your data to train public AI models',
                  'Store payment card details on our servers',
                  'Access your data without legitimate need',
                  'Sell your personal information',
                ].map((item) => (
                  <li key={item} className={`flex items-start gap-2 text-sm ${isDark ? 'text-gray-300' : 'text-slate-600'}`}>
                    <span className={`mt-0.5 ${isDark ? 'text-red-400' : 'text-red-600'}`}>✕</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Bug Bounty */}
      <section className="mb-16">
        <div className={`p-8 rounded-2xl border ${
          isDark
            ? 'bg-gradient-to-br from-purple-500/10 to-cyan-500/10 border-white/5'
            : 'bg-gradient-to-br from-purple-50 to-cyan-50 border-purple-100'
        }`}>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <h2 className={`text-2xl font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>Security Bug Bounty</h2>
              <p className={isDark ? 'text-gray-400' : 'text-slate-600'}>
                Found a security vulnerability? We offer rewards for responsible disclosure.
              </p>
            </div>
            <a
              href="mailto:security@sweetpilot.com"
              className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-xl font-medium text-white shrink-0"
            >
              Report a Vulnerability <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className={`text-center p-8 rounded-2xl border ${
        isDark ? 'bg-dark-800/50 border-white/5' : 'bg-white border-slate-200 shadow-sm'
      }`}>
        <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>Security Questions?</h2>
        <p className={`mb-6 ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
          Our security team is available to answer questions and provide additional documentation.
        </p>
        <a
          href="mailto:security@sweetpilot.com"
          className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-xl font-medium text-white"
        >
          Contact Security Team
        </a>
      </section>
    </StaticPageLayout>
  );
}
